import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CreateLeadModal } from '../leads/components/CreateLeadModal';
import { CreateActivityModal } from '../../components/activities/CreateActivityModal';
import type { Lead } from '../../types';
import { useDashboardOverview } from './hooks/useDashboardOverview';
import type { DashboardPeriod } from './types';
import { DashboardAngeboteChart } from './components/DashboardAngeboteChart';
import { DashboardEmptyState } from './components/DashboardEmptyState';
import { DashboardHeader } from './components/DashboardHeader';
import { DashboardInboxPanel } from './components/DashboardInboxPanel';
import { DashboardKpiCards } from './components/DashboardKpiCards';
import { DashboardLoadingSkeleton } from './components/DashboardLoadingSkeleton';
import { DashboardPipelinePanel } from './components/DashboardPipelinePanel';
import { DashboardTodayPanel } from './components/DashboardTodayPanel';
import { useCapabilities } from '../../hooks/useCapabilities';
import { useModuleAccess } from '../../hooks/useModuleAccess';

export function DashboardContent() {
  const [period, setPeriod] = useState<DashboardPeriod>('month');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createActivityOpen, setCreateActivityOpen] = useState(false);
  const { data, loading, refreshing, error, lastUpdatedAt, refetch } = useDashboardOverview(period);
  const { can, loaded: capabilitiesLoaded } = useCapabilities();
  const { isEnabled: isModuleEnabled } = useModuleAccess();
  const navigate = useNavigate();
  const { instanceId } = useParams<{ instanceId: string }>();
  const isPeriodSwitching = refreshing && data?.period !== period;

  const showUmsatz = can('web.rechnungen.view');
  const showAnfragen = can('web.projekte.view');
  // Angebote haben keine eigenen Rechte: Projekte-Recht plus Modul Angebote.
  const showAngebote = showAnfragen && isModuleEnabled('angebote');
  const showEmails = can('web.posteingang.view');
  const showToday = can('web.plantafel.view');
  const canCreateProject = can('web.projekte.edit');
  const canCreateActivity = can('web.projekte.edit');

  const showKpiCards = showUmsatz || showAnfragen || showEmails;
  const showLeft = showKpiCards;
  const showRight = showEmails || showToday;
  const hasAnyTile = showLeft || showRight;
  const chartModes = useMemo(
    () => ({ umsatz: showUmsatz, angebote: showAngebote, anfragen: showAnfragen }),
    [showUmsatz, showAngebote, showAnfragen],
  );

  if (loading || !capabilitiesLoaded) {
    return <DashboardLoadingSkeleton />;
  }

  if (!data) {
    return (
      <DashboardEmptyState
        message={error ?? 'Dashboard konnte nicht geladen werden.'}
        onRetry={refetch}
      />
    );
  }

  const handleLeadCreated = (lead: Lead) => {
    setCreateModalOpen(false);
    refetch();
    const leadId = encodeURIComponent(String(lead.id));
    navigate(`/i/${encodeURIComponent(instanceId ?? 'default')}/projects/${leadId}`);
  };

  return (
    <>
      <div className={`space-y-5 transition-opacity ${refreshing ? 'opacity-60' : 'opacity-100'}`}>
        <DashboardHeader
          greeting={data.greeting}
          period={period}
          onPeriodChange={setPeriod}
          onCreateProject={canCreateProject ? () => setCreateModalOpen(true) : undefined}
          showAppointmentsToday={showToday}
          refreshing={refreshing}
          error={error}
          lastUpdatedAt={lastUpdatedAt}
        />
        {isPeriodSwitching ? (
          <DashboardLoadingSkeleton />
        ) : (
          <>
            {!hasAnyTile ? (
              <div className="rounded-xl border border-app-border bg-app-surface p-8 text-center text-sm text-app-text-muted shadow-sm">
                Keine Übersicht verfügbar für deine Rechte.
              </div>
            ) : (
              <div
                className={`grid grid-cols-1 gap-4 ${showLeft && showRight ? 'lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]' : 'lg:grid-cols-1'} lg:items-start`}
              >
                {showLeft ? (
                  <div className="min-w-0 space-y-4">
                    <DashboardKpiCards
                      period={data.period}
                      kpis={data.kpis}
                      kpiComparison={data.kpiComparison}
                      syncing={false}
                      visible={{ umsatz: showUmsatz, anfragen: showAnfragen, emails: showEmails }}
                    />
                    {showUmsatz || showAnfragen ? (
                      <DashboardAngeboteChart
                        points={data.chart.points}
                        subtitle={data.chart.subtitle}
                        availableModes={chartModes}
                      />
                    ) : null}
                    {showAnfragen ? (
                      <DashboardPipelinePanel pipeline={data.pipeline} categories={data.categories} />
                    ) : null}
                  </div>
                ) : null}
                {showRight ? (
                  <div className="min-w-0 space-y-4 lg:pt-0.5">
                    {showEmails ? <DashboardInboxPanel inbox={data.inbox} /> : null}
                    {showToday ? (
                      <DashboardTodayPanel
                        items={data.today}
                        onCreateActivity={canCreateActivity ? () => setCreateActivityOpen(true) : undefined}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>

      {createModalOpen ? (
        <CreateLeadModal onClose={() => setCreateModalOpen(false)} onCreated={handleLeadCreated} />
      ) : null}

      {createActivityOpen ? (
        <CreateActivityModal
          onClose={() => setCreateActivityOpen(false)}
          onCreated={() => refetch()}
        />
      ) : null}
    </>
  );
}
